import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Send, X, Loader2 } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canAccess } from "../lib/sections";
import { TemplatePicker, PLACEHOLDER_HINT } from "./MessageTemplates";

const AGENT_CHANNELS = ["responsable"];
const btn = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-2 sm:px-3 sm:py-1.5 text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

const MessageDialog = ({ agent, onClose }) => {
  const [chans, setChans] = useState(null);
  const [ch, setCh] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get("/messages/channels").then((r) => {
      const list = r.data.filter((c) => AGENT_CHANNELS.includes(c.key));
      setChans(list); setCh(list[0]?.key || "");
    }).catch((e) => { setChans([]); toast.error(formatApiError(e)); });
  }, []);
  const send = async () => {
    if (!text.trim() || !ch) return;
    setBusy(true);
    try {
      const { data: th } = await http.post("/messages/agent-thread", { agent_id: agent.id, channel: ch });
      await http.post("/messages", { channel: ch, thread_id: th.id, content: text.trim() });
      toast.success(`Message envoyé à ${agent.first_name} dans son application`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 grid place-items-end sm:place-items-center p-0 sm:p-4">
      <div data-testid="agent-quick-message-dialog" className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl p-5 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-sky-600" />
          <h4 className="font-bold">Message à {agent.first_name} {agent.last_name}</h4>
          <button type="button" onClick={onClose} data-testid="agent-quick-message-close" className="ml-auto p-1 rounded hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-slate-500 mt-1">L'agent le reçoit dans la messagerie de son application, avec une notification.</p>
        {chans === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 mt-4" /> : chans.length === 0
          ? <p className="text-sm text-rose-600 mt-4" data-testid="agent-quick-message-no-channel">Vous n'avez accès à aucun service de messagerie agent (RH, Commercial, Exploitation).</p>
          : <>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="agent-quick-message-channels">
              {chans.map((c) => <button key={c.key} type="button" data-testid={`agent-quick-message-channel-${c.key}`} onClick={() => setCh(c.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${ch === c.key ? "bg-sky-600 text-white border-sky-600" : "border-slate-300 hover:border-slate-500"}`}>{c.label}</button>)}
            </div>
            <div className="mt-3"><TemplatePicker onPick={setText} current={text} /></div>
            <textarea data-testid="agent-quick-message-text" autoFocus rows={4} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message…"
              className="mt-3 w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20" />
            <p className="text-[11px] text-slate-500">{PLACEHOLDER_HINT} Le site et la date sont ceux de sa prochaine vacation.</p>
            <button type="button" data-testid="agent-quick-message-send" onClick={send} disabled={busy || !text.trim()}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-semibold rounded-md py-2.5 text-sm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Envoyer
            </button>
          </>}
      </div>
    </div>
  );
};

export const AgentQuickContact = ({ agent }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!agent?.id) return null;
  const phone = (agent.phone || "").replace(/[^\d+]/g, "");
  return (
    <div className="flex items-center gap-2" data-testid="agent-quick-contact">
      <a data-testid="agent-quick-call" href={phone ? `tel:${phone}` : undefined} aria-disabled={!phone} title={phone ? `Appeler ${agent.phone}` : "Aucun numéro renseigné"}
        className={`${btn} bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ${phone ? "" : "opacity-40 pointer-events-none"}`}><Phone className="h-4 w-4 sm:h-3.5 sm:w-3.5" /><span className="hidden sm:inline">Appeler</span></a>
      {canAccess(user, "messages") && (
        <button type="button" data-testid="agent-quick-message" onClick={() => setOpen(true)} className={`${btn} bg-sky-50 text-sky-700 hover:bg-sky-100`}>
          <MessageCircle className="h-4 w-4 sm:h-3.5 sm:w-3.5" /><span className="hidden sm:inline">Message</span>
        </button>
      )}
      {open && <MessageDialog agent={agent} onClose={() => setOpen(false)} />}
    </div>
  );
};
